import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Leaf, 
  Box, 
  Truck, 
  MessageSquare, 
  X, 
  Send 
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import Translate from '../components/Translate';

import { useTranslation } from 'react-i18next';
import { getLangText } from '../utils/langHelper';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useData();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const product = data?.products?.find(p => p.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const pCat = (product?.category || '').toLowerCase();
  const isFood = pCat.includes('food') || pCat === 'sugar' || pCat === 'jaggery';

  const prodName = getLangText(product, 'name', lang);
  const prodDesc = getLangText(product, 'desc', lang);
  const features = getLangText(product, 'features', lang);
  const packingSizes = getLangText(product, 'packingSizes', lang);
  const benefits = getLangText(product, 'benefits', lang);
  const packagingOptions = getLangText(product, 'packagingOptions', lang);

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!product) return;
    
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const contact = formData.get('contact');
    const email = formData.get('email');
    const quantity = formData.get('quantity');

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'inquiries'), {
        fullName: name,
        phone: contact,
        email: email,
        productName: product.name,
        quantityRequired: quantity,
        subject: `Product Inquiry: ${product.name}`,
        message: `Inquiry for ${quantity} of ${product.name}`,
        status: 'pending',
        timestamp: serverTimestamp()
      });
      
      setIsSubmitting(false);
      setShowEnquiryModal(false);
      
      Swal.fire({
        title: 'Enquiry Sent Successfully!',
        text: 'Our sales and logistics team will review your inquiry and contact you shortly.',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
    } catch (err) {
      setIsSubmitting(false);
      console.error('Error submitting enquiry:', err);
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    }
  };

  const handleWhatsAppInquiry = () => {
    if (!product) return;
    const phone = '919422000000'; // Default official WhatsApp contact
    const messageText = `Hello Sangram Cane Agro Team, I am interested in inquiring about ${product.name}. Please provide pricing and availability details.`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(messageText)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-20 h-20 bg-ivory rounded-2xl flex items-center justify-center mx-auto">
            <Leaf className="text-slate/30" size={40} />
          </div>
          <h1 className="text-3xl font-heading font-extrabold text-charcoal">
            <Translate>Product Not Found</Translate>
          </h1>
          <p className="text-slate">
            <Translate>The product you are looking for might have been moved or deleted.</Translate>
          </p>
          <button 
            onClick={() => navigate('/products')}
            className="w-full btn-premium cursor-pointer"
          >
            <Translate>Browse All Products</Translate>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8 min-h-screen bg-ivory pb-20 font-poppins">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Breadcrumb */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200/60">
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center space-x-2 text-slate hover:text-emerald transition-colors group text-xs font-heading font-bold uppercase tracking-wider cursor-pointer"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
            <span><Translate>Back to catalog</Translate></span>
          </button>
          <div className="hidden sm:flex items-center space-x-2 text-xs font-heading font-semibold text-slate/50">
            <span className="hover:text-emerald cursor-pointer transition-colors" onClick={() => navigate('/products')}>
              <Translate>Catalog</Translate>
            </span>
            <span className="text-slate/20">/</span>
            <span><Translate>{isFood ? 'Food Products' : 'Industrial & Agricultural Products'}</Translate></span>
            <span className="text-slate/20">/</span>
            <span className="text-emerald font-bold"><Translate>{prodName}</Translate></span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative p-2 bg-white rounded-2xl border border-gray-100 shadow-premium">
              <div className="rounded-xl overflow-hidden aspect-square relative bg-mist">
                <img 
                  src={product.image} 
                  alt={prodName}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Info Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-7 space-y-8"
          >
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-charcoal tracking-tight mb-4 leading-tight">
                <Translate>{prodName}</Translate>
              </h1>
              <div className="gold-divider mb-6"></div>
              <p className="text-base lg:text-lg text-slate leading-relaxed">
                <Translate>{prodDesc}</Translate>
              </p>
            </div>

            {/* Key Selling Features for Food Products */}
            {isFood && features && features.length > 0 && (
              <div>
                <h3 className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-slate/50 mb-4">
                  <Translate>Key Selling Features</Translate>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white p-4 rounded-xl border border-gray-100 hover:border-emerald/20 transition-all duration-300 group">
                      <div className="w-8 h-8 bg-emerald/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald/15 transition-colors">
                        <CheckCircle2 className="text-emerald" size={16} />
                      </div>
                      <span className="font-heading font-semibold text-charcoal text-sm">
                        <Translate>{feature}</Translate>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Packing Available for Food Products */}
            {isFood && packingSizes && packingSizes.length > 0 && (
              <div>
                <h3 className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-slate/50 mb-4 flex items-center">
                  <Box size={14} className="mr-1.5 text-emerald" />
                  <Translate>Packing Available</Translate>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {packingSizes.map((size, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-xl border border-emerald/20 text-center shadow-sm hover:shadow-md transition-all">
                      <span className="block text-xs font-heading font-bold text-slate/50 uppercase tracking-wider mb-1">
                        <Translate>Package Size</Translate>
                      </span>
                      <span className="text-sm font-heading font-extrabold text-emerald">
                        <Translate>{size}</Translate>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Benefits for Industrial & Agricultural Products */}
            {!isFood && benefits && benefits.length > 0 && (
              <div>
                <h3 className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-slate/50 mb-4 flex items-center">
                  <CheckCircle2 size={14} className="mr-1.5 text-gold" />
                  <Translate>Key Benefits</Translate>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white p-4 rounded-xl border border-gray-100 hover:border-gold/30 transition-all duration-300 group">
                      <div className="w-8 h-8 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                        <CheckCircle2 className="text-gold" size={16} />
                      </div>
                      <span className="font-heading font-semibold text-charcoal text-sm">
                        <Translate>{benefit}</Translate>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Packaging & Supply for Industrial & Agricultural Products */}
            {!isFood && packagingOptions && packagingOptions.length > 0 && (
              <div>
                <h3 className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-slate/50 mb-4 flex items-center">
                  <Truck size={14} className="mr-1.5 text-emerald" />
                  <Translate>Packaging & Supply</Translate>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {packagingOptions.map((opt, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200/80 text-center shadow-sm hover:shadow-md transition-all">
                      <span className="block text-xs font-heading font-bold text-slate/50 uppercase tracking-wider mb-1">
                        <Translate>Supply Option</Translate>
                      </span>
                      <span className="text-sm font-heading font-extrabold text-charcoal">
                        <Translate>{opt}</Translate>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features list fallback for Industrial if benefits missing */}
            {!isFood && (!product.benefits || product.benefits.length === 0) && product.features && product.features.length > 0 && (
              <div>
                <h3 className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-slate/50 mb-4">
                  <Translate>Key Features</Translate>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white p-4 rounded-xl border border-gray-100">
                      <CheckCircle2 className="text-emerald" size={16} />
                      <span className="font-heading font-semibold text-charcoal text-sm">
                        <Translate>{feature}</Translate>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2 CTA Buttons (Product Enquiry Modal & WhatsApp Integration) */}
            <div className="pt-6 border-t border-gray-200/80 flex flex-col sm:flex-row gap-4">
              {/* Button 1: Product Enquiry Modal Button */}
              <button
                onClick={() => setShowEnquiryModal(true)}
                className="flex-1 bg-forest hover:bg-[#12281E] text-white py-4 px-6 rounded-xl font-heading font-bold uppercase tracking-wider text-xs flex items-center justify-center space-x-2 transition-all duration-300 shadow-md cursor-pointer"
              >
                <MessageSquare size={16} />
                <span><Translate>Product Enquiry</Translate></span>
              </button>

              {/* Button 2: WhatsApp Inquiry Button */}
              <button
                onClick={handleWhatsAppInquiry}
                className="flex-1 bg-[#25D366] hover:bg-[#128C7E] text-white py-4 px-6 rounded-xl font-heading font-bold uppercase tracking-wider text-xs flex items-center justify-center space-x-2 transition-all duration-300 shadow-md cursor-pointer"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span><Translate>Inquire via WhatsApp</Translate></span>
              </button>
            </div>

          </motion.div>
        </div>
      </div>

      {/* Product Enquiry Modal */}
      <AnimatePresence>
        {showEnquiryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl relative border border-gray-100 p-6 md:p-8"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                <div>
                  <span className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-emerald bg-emerald/10 px-3 py-1 rounded-full border border-emerald/10">
                    Product Enquiry
                  </span>
                  <h3 className="text-xl font-heading font-bold text-charcoal mt-2">
                    {product.name}
                  </h3>
                </div>
                <button
                  onClick={() => setShowEnquiryModal(false)}
                  className="w-9 h-9 bg-ivory hover:bg-mist text-charcoal rounded-full flex items-center justify-center transition-colors cursor-pointer border border-gray-100"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleEnquirySubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-heading font-bold uppercase text-slate/70 mb-1.5">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 text-xs font-heading font-bold text-charcoal outline-none focus:border-emerald focus:bg-white transition-colors"
                    placeholder="e.g. Rahul Sharma"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-heading font-bold uppercase text-slate/70 mb-1.5">
                      Contact Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contact"
                      required
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 text-xs font-heading font-bold text-charcoal outline-none focus:border-emerald focus:bg-white transition-colors"
                      placeholder="e.g. +91 9876543210"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-heading font-bold uppercase text-slate/70 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 text-xs font-heading font-bold text-charcoal outline-none focus:border-emerald focus:bg-white transition-colors"
                      placeholder="e.g. rahul@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-heading font-bold uppercase text-slate/70 mb-1.5">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="productName"
                    value={product.name}
                    disabled
                    className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-xs font-heading font-extrabold text-slate cursor-not-allowed opacity-80"
                  />
                </div>

                <div>
                  <label className="block text-xs font-heading font-bold uppercase text-slate/70 mb-1.5">
                    Quantity Required <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="quantity"
                    required
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 text-xs font-heading font-bold text-charcoal outline-none focus:border-emerald focus:bg-white transition-colors"
                    placeholder="e.g. 500 kg / 10 Tons / 100 Bags"
                  />
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowEnquiryModal(false)}
                    className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-charcoal rounded-xl text-xs font-heading font-bold transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-premium px-6 py-3 text-xs flex items-center space-x-2 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <Send size={14} />
                    )}
                    <span>{isSubmitting ? 'Submitting...' : 'Submit Enquiry'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetail;
